<template>
  <div class="donation-container">
    <div class="donation-content" v-if="donation">
      <!-- Header Section -->
      <div class="header-section">
        <div class="header-background"></div>
        <h1 class="page-title">Donation Receipt</h1>
        <p class="page-subtitle">Thank you for your generous contribution</p>
                  <div class="status-pill" :class="getStatusClass(donation.status)"  v-if="donation">
            <v-icon small>{{ getStatusIcon(donation.status) }}</v-icon>
            <span>{{ donation.status }}</span>
          </div>
      </div>
      
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <v-progress-circular
          indeterminate
          color="primary"
          size="60"
          width="5"
        />
        <span class="loading-text">Loading your donation details...</span>
      </div>
      
      <!-- Content when loaded -->
      <div v-else class="donation-details">
        <!-- Status Banner -->
        <div class="status-banner" :class="getStatusClass(donation.status)" v-if="donation">
          <v-icon large class="status-icon">{{ getStatusIcon(donation.status) }}</v-icon>
          <div class="status-content">
            <h3 class="status-title">{{ getStatusTitle(donation.status) }}</h3>
            <p class="status-description">{{ donation.status_description }}</p>
            <p class="status-date">{{ formatDate(donation.createdAt) }}</p>
          </div>
        </div>
        
        
        <!-- Parties Section -->
        <div class="parties-section" v-if="donation">
          <!-- Sender Card -->
          <v-card class="party-card sender-card" elevation="0">
            <div class="card-header">
              <v-icon color="white">mdi-account-outline</v-icon>
              <h3>Donor Information</h3>
            </div>
            <div class="card-content">
              <div class="party-details">
                <agency-avatar
                  :img="donation.payer.midThumbnail"
                  :name="donation.payer.name"
                  :size="60"
                  class="party-avatar"
                />
                <div class="party-info">
                  <h4 class="party-name">{{ donation.payer.name }}</h4>
                  <div class="party-meta">
                    <span class="party-type">{{ donation.payer_type }}</span>
                    <span v-if="donation.payer.email" class="party-email">
                      <v-icon small>mdi-email</v-icon>
                      {{ donation.payer.email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
          
          <!-- Receiver Card -->
          <v-card 
            class="party-card receiver-card" 
            elevation="0"
            @click="goToProfile"
          >
            <div class="card-header">
              <v-icon color="white">mdi-account-heart-outline</v-icon>
              <h3>Recipient Organization</h3>
            </div>
            <div class="card-content">
              <div class="party-details">
                <agency-avatar
                  :img="donation.receiver.midThumbnail || donation.receiver.image"
                  :name="donation.receiver.name"
                  :size="60"
                  class="party-avatar"
                />
                <div class="party-info">
                  <h4 class="party-name">{{ donation.receiver.name }}</h4>
                  <div class="party-meta">
                    <span class="party-type">{{ donation.receiver_type }}</span>
                    <span v-if="donation.receiver.email" class="party-email">
                      <v-icon small>mdi-email</v-icon>
                      {{ donation.receiver.email }}
                    </span>
                  </div>
                </div>
              </div>
              <v-icon class="chevron">mdi-chevron-right</v-icon>
            </div>
          </v-card>
        </div>
        
        <!-- Summary Cards -->
        <div class="summary-cards">
          <!-- Amount Summary Card -->
          <v-card class="summary-card amount-summary" elevation="0">
            <div class="card-header">
              <v-icon color="white">mdi-cash-multiple</v-icon>
              <h3>Payment Summary</h3>
            </div>
            <div class="card-content" v-if="donation">
              <div class="amount-display">
                <span class="amount-label">Total Donation Amount</span>
                <span class="amount-value primary--text">{{ formatCurrency(donation.pay_in / 100) }}</span>
              </div>
              
              <div class="breakdown">
                <div class="breakdown-item">
                  <span class="breakdown-label">Processing Fee 
                            <span>
                              ({{ donation.commision_percentage }}%)
                            </span>
                  </span>
                  <span class="breakdown-value">{{ formatCurrency(calculateProcessingFee()) }}</span>
                </div>
                
                <v-divider class="my-2"></v-divider>
                
                <div class="breakdown-item total">
                  <span class="breakdown-label">Net Amount to Organization</span>
                  <span class="breakdown-value success--text">
                      {{ formatCurrency(calculateNetAmount()) }}
                    </span>
                </div>
              </div>
            </div>
          </v-card>
          
          <!-- Details Summary Card -->
          <v-card class="summary-card details-summary" elevation="0" v-if="donation">
            <div class="card-header">
              <v-icon color="white">mdi-file-document-outline</v-icon>
              <h3>Transaction Details</h3>
            </div>
            <div class="card-content" v-if="donation">
              <div class="detail-item">
                <v-icon small>mdi-calendar-clock</v-icon>
                <div class="detail-content" v-if="donation">
                  <span class="detail-label">Processed On</span>
                  <span class="detail-value">{{ formatDate(donation.createdAt) }}</span>
                </div>
              </div>
              
              <div class="detail-item">
                <v-icon small>mdi-identifier</v-icon>
                <div class="detail-content">
                  <span class="detail-label">Transaction ID</span>
                  <span class="detail-value">{{ donation._id }}</span>
                </div>
              </div>
              
              <div class="detail-item" v-if="donation.payment_intent">
                <v-icon small>mdi-credit-card-check</v-icon>
                <div class="detail-content">
                  <span class="detail-label">Payment Reference</span>
                  <span class="detail-value">{{ donation.payment_intent }}</span>
                </div>
              </div>
              
              <div class="detail-item" v-if="donation.payment_method">
                <v-icon small>mdi-credit-card-outline</v-icon>
                <div class="detail-content">
                  <span class="detail-label">Payment Method</span>
                  <span class="detail-value">{{ donation.payment_method }}</span>
                </div>
              </div>
            </div>
          </v-card>
        </div>
        
        <!-- Message Card -->
        <v-card class="message-card" elevation="0" v-if="donation.description">
          <div class="message-header">
            <v-icon color="primary">mdi-message-text-outline</v-icon>
            <h3>Donor's Message</h3>
          </div>
          <div class="message-content">
            <p class="message-text">{{ donation.description }}</p>
          </div>
        </v-card>
        
        <!-- Actions -->
        <div class="action-buttons">
          <!-- <v-btn
            class="print-btn"
            color="primary"
            dark
            @click="printReceipt"
          >
            <v-icon left>mdi-printer</v-icon>
            Print Receipt
          </v-btn> -->
          
          <v-btn
            class="share-btn"
            color="primary"
            @click="shareReceipt"
          >
            <v-icon left>mdi-share-variant</v-icon>
            Share
          </v-btn>
          
          <!-- <v-btn
            class="download-btn"
            color="secondary"
            @click="downloadReceipt"
          >
            <v-icon left>mdi-download</v-icon>
            Download PDF
          </v-btn> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AgencyAvatar from "@/components/agency/AgencyAvatar.vue";
import dateFormat from "dateformat";

export default {
  components: { AgencyAvatar },
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      donation: null,
    };
  },
  methods: {
    ...mapActions({
      getDonation: "donation/getDonation"
    }),
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    goToProfile() {
      let routeData = this.$router.resolve({
        name: "agency-profile-details",
        params: { id: this.donation.receiver._id }
      });
      window.open(routeData.href, "_blank");
    },
    formatCurrency(numberString) {
      const number = parseFloat(numberString);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(number);
    },
    formatDate(date) {
      return dateFormat(
        this.convertTZ(date, this.timezone),
        "mmmm d, yyyy 'at' h:MM TT"
      );
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    },
    getStatusClass(status) {
      const statusMap = {
        'paid': 'success',
        'pending': 'warning',
        'failed': 'error',
        'processing': 'info'
      };
      return statusMap[status.toLowerCase()] || 'grey';
    },
    getStatusIcon(status) {
      const statusLower = status.toLowerCase();
      if (statusLower === 'paid') return 'mdi-check-circle';
      if (statusLower === 'pending') return 'mdi-clock';
      if (statusLower === 'failed') return 'mdi-alert-circle';
      return 'mdi-information';
    },
    getStatusTitle(status) {
      const statusLower = status.toLowerCase();
      if (statusLower === 'paid') return 'Donation Successful!';
      if (statusLower === 'pending') return 'Donation Processing';
      if (statusLower === 'failed') return 'Donation Failed';
      return 'Donation Status';
    },
    printReceipt() {
      window.print();
    },
    shareReceipt() {
      if (navigator.share) {
        navigator.share({
          title: 'My Donation Receipt',
          text: `I donated to ${this.donation.receiver.name} through Akindr!`,
          url: window.location.href,
        }).catch(err => {
          console.log('Error sharing:', err);
        });
      } else {
        this.showErrorMessage('Web Share not supported in your browser');
      }
    },
    calculateProcessingFee() {
      if (!this.donation ) return 0;
      const amount = this.donation.pay_in / 100; // Convert from cents to dollars
      return amount * (this.donation.commision_percentage / 100);
    },
    
    calculateNetAmount() {
      if (!this.donation) return 0;
      const amount = this.donation.pay_in / 100; // Convert from cents to dollars
      const fee = this.calculateProcessingFee();
      return amount - fee;
    },
    downloadReceipt() {
      // In a real implementation, this would generate a PDF
      this.showErrorMessage('PDF generation will be implemented');
    }
  },
  computed: {
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    })
  },
  mounted() {
    this.loading = true;
    this.getDonation({ _id: this.$route.query._id })
      .then(res => {
        this.loading = false;
        this.donation = res;
        // Ensure payer information exists
        if (!this.donation.payer) {
          this.donation.payer = {
            name: "Anonymous Donor",
            image: null,
            type: "Individual"
          };
        }
      })
      .catch(error => {
        this.loading = false;
        if (error.response == undefined) {
          console.log(error);
        } else {
          this.showErrorMessage(error.response.data.message);
        }
      });
  }
};
</script>

<style scoped>
/* Base Styles */
.donation-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  /* background-color: #f8f9fa; */
}

.donation-content {
  width: 100%;
  max-width: 800px;
  padding: 0 16px 40px;
  box-sizing: border-box;
}

/* Header Section */
.header-section {
  margin-bottom: 24px;
  text-align: center;
  position: relative;
  padding: 40px 16px 30px;
  background-color: #084468;
  color: white;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-left: -16px;
  margin-right: -16px;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
  border-radius: 0 0 20px 20px;
  z-index: 0;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  position: relative;
  margin-bottom: 8px;
  z-index: 1;
}

.page-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  z-index: 1;
  margin-bottom: 0;
  font-family: monospace;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 24px;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  color: #084468;
}

/* Status Banner */
.status-banner {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  color: #fff;
  background: linear-gradient(135deg, #4CAF50 0%, #81C784 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.status-banner.success {
  border-left: 4px solid #4CAF50;
}

.status-banner.warning {
  border-left: 4px solid #FFC107;
}

.status-banner.error {
  border-left: 4px solid #F44336;
}

.status-banner.info {
  border-left: 4px solid #2196F3;
}

.status-icon {
  margin-right: 16px;
  flex-shrink: 0;
}

.status-content {
  flex: 1;
}

.status-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #fff;
}

.status-description {
  font-size: 14px;
  color: #fff;
  margin-bottom: 4px;
}

.status-date {
  font-size: 12px;
  color: #fff;
  margin-bottom: 0;
}

/* Parties Section */
.parties-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

@media (min-width: 768px) {
  .parties-section {
    grid-template-columns: 1fr 1fr;
  }
}

.party-card {
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.party-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.sender-card .card-header {
  background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
}

.receiver-card .card-header {
  background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
}

.card-header {
  display: flex;
  align-items: center;
  padding: 16px;
  color: white;
}

.card-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin-left: 12px;
  margin-bottom: 0;
}

.card-header i {
  font-size: 20px;
}

.card-content {
  padding: 16px;
  background-color: white;
  position: relative;
}

.receiver-card .card-content {
  padding-right: 40px;
}

.party-details {
  display: flex;
  align-items: center;
}

.party-avatar {
  margin-right: 16px;
  flex-shrink: 0;
}

.party-info {
  flex: 1;
}

.party-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.party-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
}

.party-type {
  background-color: #f0f0f0;
  color: #666;
  padding: 2px 8px;
  border-radius: 10px;
}

.party-email {
  color: #666;
  display: flex;
  align-items: center;
}

.party-email i {
  margin-right: 4px;
  font-size: 14px;
}

.chevron {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #4CAF50;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

@media (min-width: 768px) {
  .summary-cards {
    grid-template-columns: 1fr 1fr;
  }
}

.summary-card {
  border-radius: 12px;
  overflow: hidden;
}

.amount-summary .card-header {
  background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
}

.details-summary .card-header {
  /* background: linear-gradient(135deg, #607D8B 0%, #90A4AE 100%); */
    background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
}

/* Amount Display */
.amount-display {
  text-align: center;
  margin-bottom: 16px;
  padding: 16px 0;
  border-bottom: 1px dashed #eee;
}

.amount-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.amount-value {
  font-size: 28px;
  font-weight: 700;
  color: #673AB7;
}

/* Breakdown */
.breakdown-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}

.breakdown-label {
  color: #666;
}

.breakdown-value {
  font-weight: 500;
}

.breakdown-item.total {
  padding-top: 12px;
  border-top: 1px dashed #eee;
  margin-top: 8px;
}

.breakdown-item.total .breakdown-label {
  font-weight: 600;
  color: #333;
}

.breakdown-item.total .breakdown-value {
  font-weight: 700;
  color: #4CAF50;
  font-size: 16px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  margin: 8px 0 0 0px;
}

.status-pill.success {
  background-color: rgba(0, 184, 148, 0.2);
}

.status-pill.pending {
  background-color: rgba(253, 203, 110, 0.2);
}

.status-pill.error {
  background-color: rgba(255, 118, 117, 0.2);
}

.status-pill.processing {
  background-color: rgba(116, 185, 255, 0.2);
}

.status-pill i {
  margin-right: 6px;
}

/* Detail Items */
.detail-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item i {
  color: #607D8B;
  margin-right: 12px;
}

.detail-content {
  flex: 1;
}

.detail-label {
  display: block;
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  word-break: break-all;
}

/* Message Card */
.message-card {
  border-radius: 12px;
  margin-bottom: 24px;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.message-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.message-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin-left: 12px;
  margin-bottom: 0;
  color: #333;
}

.message-header i {
  font-size: 20px;
}

.message-content {
  padding: 16px;
}

.message-text {
  font-size: 15px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.print-btn, .share-btn, .download-btn {
  flex: 1;
  min-width: 120px;
  border-radius: 8px;
  height: 48px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
}

.share-btn {
  background: linear-gradient(135deg, #084468 0%, #0d6b99 100%);
  box-shadow: 0 4px 12px rgba(8, 68, 104, 0.2);
}

.download-btn {
  background: linear-gradient(135deg, #607D8B 0%, #90A4AE 100%);
}

/* Responsive Styles */
@media (max-width: 600px) {
  .page-title {
    font-size: 24px;
  }
  
  .page-subtitle {
    font-size: 12px;
  }
  
  .parties-section, .summary-cards {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .print-btn, .share-btn, .download-btn {
    width: 100%;
  }
}

@media print {
  .donation-container {
    background-color: white;
  }
  
  .donation-content {
    padding: 0;
    max-width: 100%;
  }
  
  .header-section {
    background-color: white !important;
    color: #084468 !important;
    padding: 20px 0;
    margin-bottom: 20px;
  }
  
  .page-title, .page-subtitle {
    color: #084468 !important;
  }
  
  .party-card, .summary-card, .message-card {
    box-shadow: none !important;
    border: 1px solid #eee;
    margin-bottom: 20px;
  }
  
  .action-buttons {
    display: none;
  }
}
</style>